import React from 'react';

import isEmpty from 'lodash/isEmpty';

import { CONTENT_ACTIONS } from '@lumapps/contents/constants';
import type { ContentAction } from '@lumapps/contents/types';
import { ContentEditorContext } from '@lumapps/designer-content-editor/context/ContentEditorContext';
import { getWidgetsDirtyTextualFields } from '@lumapps/designer-content-editor/utils/getWidgetsDirtyTextualFields';
import { useContentValidity } from '@lumapps/designer/hooks/useContentValidity';
import { isFeatureEnabled } from '@lumapps/features';
import { useFormContext } from '@lumapps/lumx-forms';
import { useBooleanState } from '@lumapps/lumx-hooks/hooks/useBooleanState';
import { useSelector } from '@lumapps/redux/react';
import { contributionLanguagesSelector } from '@lumapps/translations/ducks/selectors';

import { MissingTranslationDialog } from '../components/MissingTranslationDialog';
import { useApplyWidgetTranslations } from './useApplyWidgetTranslations';

/**
 * Hook allowing the display of the missing translation dialog, used to allow the user to auto-translate
 * his content before saving/publishing/scheduling it
 */
export const useMissingTranslationDialog = () => {
    const { widgetPaths } = React.useContext(ContentEditorContext);
    const formContext = useFormContext();
    const { formState, getValues, reset } = formContext ?? {};

    const isEndUserTranslationEnabled = useSelector(isFeatureEnabled('translation-end-user'));
    const contributionLanguages = useSelector(contributionLanguagesSelector);

    const isContentValid = useContentValidity();
    const inputLang = formContext ? formContext.watch('lang') : undefined;

    const [isDialogOpen, , closeDialog, openDialog] = useBooleanState(false);
    const [translatedLanguages, setTranslatedLanguages] = React.useState<string[]>([]);
    const [currentActionType, setCurrentActionType] = React.useState<ContentAction>(CONTENT_ACTIONS.SAVE);
    const [frozenTargetLanguages, setFrozenTargetLanguages] = React.useState<string[]>([]);
    const resolveRef = React.useRef<((value: boolean) => void) | null>(null);

    const dirtyTextualFields = React.useMemo(
        () => (formState ? getWidgetsDirtyTextualFields(widgetPaths, formState.dirtyFields) : {}),
        [formState, widgetPaths],
    );

    const targetLanguages = React.useMemo(
        () =>
            contributionLanguages.filter((lang) => {
                if (lang === inputLang) {
                    return false;
                }

                // Include this lang only if at least one source-dirty field is NOT dirty in this target
                return Object.values(dirtyTextualFields).some(
                    ({ header, body, footer }) =>
                        (header?.[inputLang] && !header?.[lang]) ||
                        (body?.[inputLang] && !body?.[lang]) ||
                        (footer?.[inputLang] && !footer?.[lang]),
                );
            }),
        [contributionLanguages, inputLang, dirtyTextualFields],
    );

    const onSuccess = React.useCallback(
        (translatedValues: Record<string, (string | undefined)[]>) => {
            // Reset defaults to current values so future edits of users will count as dirty.
            // Useful in the case the user cancel the flow after an auto-translate
            reset?.(getValues?.(), { keepErrors: true, keepTouched: true, keepIsSubmitted: true });
            setTranslatedLanguages((prev) => [...new Set([...prev, ...Object.keys(translatedValues)])]);
        },
        [reset, getValues],
    );

    const { isLoading, onTranslate, onHardReset } = useApplyWidgetTranslations({ onSuccess });

    const handleConfirm = React.useCallback(() => {
        onHardReset();
        setTranslatedLanguages([]);
        closeDialog();
        resolveRef.current?.(true);
        resolveRef.current = null;
    }, [closeDialog, onHardReset]);

    const handleCancel = React.useCallback(() => {
        onHardReset();
        setTranslatedLanguages([]);
        closeDialog();
        resolveRef.current?.(false);
        resolveRef.current = null;
    }, [closeDialog, onHardReset]);

    const handleTranslateChanges = React.useCallback(() => {
        if (!inputLang) {
            return;
        }

        // Errors are surfaced to the user via useWidgetsAutoTranslate's hasError state and notification.
        onTranslate({
            sourceLanguage: inputLang,
            targetLanguages: frozenTargetLanguages,
            dirtyFields: dirtyTextualFields,
        }).catch(() => {});
    }, [onTranslate, inputLang, frozenTargetLanguages, dirtyTextualFields]);

    /**
     * The dialog should be displayed only when all conditions are met :
     * - we are in the Page Writer Editor (inside a form context + a ContentEditorContext)
     * - auto-translate FF is enabled
     * - the instance has more than one language
     */
    const shouldDisplayDialog =
        !!formState && !isEmpty(widgetPaths) && isEndUserTranslationEnabled && contributionLanguages.length > 1;

    /** Check for missing translation on the updated widgets and
     * open the MissingTranslationDialog if needed
     */
    const checkMissingTranslation = React.useCallback(
        (actionType: ContentAction): Promise<boolean> => {
            if (
                // Feature flags and form context must be available
                !isContentValid ||
                !formState ||
                !isEndUserTranslationEnabled ||
                // No widget textual fields have been modified
                Object.keys(dirtyTextualFields).length === 0 ||
                // No target languages need translation (e.g. user switched input language)
                targetLanguages.length === 0 ||
                // All target languages were already auto-translated in a previous attempt
                targetLanguages.every((lang) => translatedLanguages.includes(lang))
            ) {
                return Promise.resolve(true);
            }

            const dirtyLanguages = new Set(
                Object.values(dirtyTextualFields).flatMap(({ header, body, footer }) =>
                    [header, body, footer]
                        .filter((field): field is Record<string, boolean> => !!field)
                        .flatMap(Object.keys),
                ),
            );

            if (dirtyLanguages.size >= contributionLanguages.length) {
                return Promise.resolve(true);
            }

            return new Promise<boolean>((resolve) => {
                onHardReset();
                setFrozenTargetLanguages(targetLanguages);
                resolveRef.current = resolve;
                setCurrentActionType(actionType);
                openDialog();
            });
        },
        [
            isContentValid,
            formState,
            isEndUserTranslationEnabled,
            dirtyTextualFields,
            contributionLanguages.length,
            onHardReset,
            openDialog,
            targetLanguages,
            translatedLanguages,
        ],
    );

    const missingTranslationDialog = shouldDisplayDialog ? (
        <MissingTranslationDialog
            isOpen={isDialogOpen}
            isLoading={isLoading}
            targetLanguages={frozenTargetLanguages}
            translatedLanguages={translatedLanguages}
            currentActionType={currentActionType}
            onConfirm={handleConfirm}
            onCancel={handleCancel}
            onTranslateChanges={handleTranslateChanges}
        />
    ) : null;

    return { checkMissingTranslation, missingTranslationDialog };
};
